import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchTranscriptByAppointment } from "../../api/transcript";
import LoadingCard from "./LoadingCard";
import { MessagesSquare } from "lucide-react";
import PostCallFallback from "./PostCallFallback";

const POST_CALL_WINDOW_MS = 10 * 60 * 1000;

const getTranscriptText = (response) =>
  response?.data?.data?.full_conversation ??
  response?.data?.full_conversation ??
  response?.full_conversation ??
  "";

const Transcript = ({
  appointmentId,
  username,
  callEndTime,
  postCallWindowExpired = false,
  finalFetchStatus = "idle",
}) => {
  const [manualRetrying, setManualRetrying] = useState(false);

  const deadlineExpired = useMemo(() => {
    if (postCallWindowExpired) return true;
    if (!callEndTime) return false;

    const endTime = new Date(callEndTime).getTime();

    return (
      Number.isFinite(endTime) &&
      Date.now() >= endTime + POST_CALL_WINDOW_MS
    );
  }, [callEndTime, postCallWindowExpired]);

  const queryKey = ["transcript", appointmentId, username];

  const { data, isLoading, isFetching, refetch, } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await fetchTranscriptByAppointment(
        `${username}_${appointmentId}_transcription`,
        username,
        signal
      );

      const transcriptText = getTranscriptText(response);

      if (
        typeof transcriptText !== "string" ||
        !transcriptText.trim()
      ) {
        throw new Error("Transcript is not available yet.");
      }

      return response;
    },
    enabled: Boolean(username && appointmentId && !deadlineExpired),
    retry: 3,
    staleTime: Infinity,
    gcTime: Infinity,
    retryOnMount: true,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const handleRetry = async () => {
    if (deadlineExpired || isFetching) return;

    setManualRetrying(true);

    try {
      await refetch();
    } finally {
      setManualRetrying(false);
    }
  };

  const transcriptText = getTranscriptText(data);

  const fullConversation = useMemo(() => {
    if (!transcriptText.trim()) return null;

    return transcriptText.split(/\r?\n/).map((convo, index) => {
      if (!convo.trim() || convo.trim() === ":") {
        return <br key={index} />;
      }

      if (!convo.includes(":")) {
        return (
          <span key={index}>
            {convo}
            <br />
          </span>
        );
      }

      const parts = convo.split(":");
      const speaker = parts.shift()?.trim();
      const conversation = parts.join(":").trim();

      return (
        <span key={index}>
          <strong> {speaker}: </strong>{" "}
          {conversation}
          <br />
        </span>
      );
    });
  }, [transcriptText]);

  const finalFetchPending =
    deadlineExpired && finalFetchStatus === "fetching";

  if (
    (!deadlineExpired && (isLoading || isFetching || manualRetrying)) ||
    finalFetchPending
  ) {
    return (
      <LoadingCard message="Processing clinical chatter… stay with us." />
    );
  }

  // Successful state: preserve the original DEV HTML.
  if (transcriptText.trim()) {
    return (
      <div>
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-medium">Call Transcript</h3>
          <div className="flex space-x-2" />
        </div>

        <div className="border rounded p-6 overflow-y-scroll text-neutral-700 max-h-[320px] whitespace-pre-wrap">
          {fullConversation}
        </div>
      </div>
    );
  }

  return (
    <PostCallFallback
      title="Call Transcript"
      icon={<MessagesSquare size={20} />}
      message={
        deadlineExpired
          ? "No transcript data available."
          : "Transcript is not available yet. You can try again."
      }
      deadlineExpired={deadlineExpired}
      onRetry={handleRetry}
      isRetrying={manualRetrying || isFetching}
      retryLabel="Retry Transcript"
      retryingLabel="Retrying Transcript…"
    />
  );
};

export default Transcript;
